import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import {
  usePurchaseOrders,
  usePurchaseOrder,
  useCreateReturn,
  type PurchaseOrderLineItem,
} from '@/hooks';
import { useNotify } from '@/stores/notificationStore';
import { ApiError } from '@/lib/api';

interface ReturnFormDialogProps {
  open: boolean;
  onClose: () => void;
}

// Per-line editable state while composing a return.
interface LineDraft {
  selected: boolean;
  quantity: number;
  reason: string;
}

const REASON_KEYS = ['defective', 'wrong_part', 'excess'] as const;
const REASON_LABEL_KEY: Record<string, string> = {
  defective: 'returns.reasonDefective',
  wrong_part: 'returns.reasonWrongPart',
  excess: 'returns.reasonExcess',
};

/**
 * Create a return-to-supplier (RMA). The user picks a purchase order, then the
 * received lines to send back, a quantity (capped at the received quantity and
 * at what is on hand in the order's warehouse, #2859) and a reason per line.
 * Submitting decrements stock and records a return_to_supplier movement
 * server-side (#229).
 */

/** The most that can go back: received, but never more than is on the shelf. */
function returnCap(line: PurchaseOrderLineItem): number {
  return line.quantityOnHand == null
    ? line.quantityReceived
    : Math.max(0, Math.min(line.quantityReceived, line.quantityOnHand));
}
export function ReturnFormDialog({ open, onClose }: ReturnFormDialogProps) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const [selectedPoId, setSelectedPoId] = useState<string>('');
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});

  const poListQuery = usePurchaseOrders();
  const purchaseOrders = useMemo(() => {
    const pages = poListQuery.data?.pages ?? [];
    return pages.flatMap((page) => page.data);
  }, [poListQuery.data]);

  const detailQuery = usePurchaseOrder(open && selectedPoId ? selectedPoId : undefined);
  const detail = detailQuery.data;
  const createReturn = useCreateReturn();

  // Only lines that have actually been received can be returned.
  const returnableLines = useMemo<PurchaseOrderLineItem[]>(
    () => (detail?.items ?? []).filter((l) => l.quantityReceived > 0),
    [detail],
  );

  const handleSelectPo = (poId: string) => {
    setSelectedPoId(poId);
    setDrafts({});
  };

  const draftFor = (line: PurchaseOrderLineItem): LineDraft =>
    drafts[line.id] ?? { selected: false, quantity: returnCap(line), reason: 'defective' };

  const anyLineCapped = returnableLines.some((line) => returnCap(line) < line.quantityReceived);

  const updateDraft = (line: PurchaseOrderLineItem, patch: Partial<LineDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [line.id]: { ...draftFor(line), ...patch },
    }));
  };

  const selectedLines = useMemo(
    () =>
      returnableLines
        .map((line) => ({ line, draft: draftFor(line) }))
        .filter((entry) => entry.draft.selected && entry.draft.quantity > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [returnableLines, drafts],
  );

  const totalReturnCents = useMemo(
    () => selectedLines.reduce((sum, { line, draft }) => sum + line.unitCostCents * draft.quantity, 0),
    [selectedLines],
  );

  const formatPrice = (cents: number) => `€${(cents / 100).toFixed(2)}`;

  const handleClose = () => {
    setSelectedPoId('');
    setDrafts({});
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedPoId || selectedLines.length === 0) return;
    try {
      await createReturn.mutateAsync({
        purchaseOrderId: selectedPoId,
        lines: selectedLines.map(({ line, draft }) => ({
          purchaseOrderLineId: line.id,
          quantity: draft.quantity,
          reason: draft.reason,
        })),
      });
      notify.success(t('returns.createdSuccess'), t('returns.createdSuccessDesc'));
      handleClose();
    } catch (error) {
      // 409 is the stock guard: the shelf has less than was asked (#2859).
      const insufficientStock = error instanceof ApiError && error.status === 409;
      notify.error(
        tc('error'),
        insufficientStock ? t('returns.createFailedInsufficientStock') : t('returns.createFailed'),
      );
    }
  };

  const canSubmit = selectedLines.length > 0 && !createReturn.isPending;

  return (
    <ResponsiveDialog
      mobileTitle={t('returns.createReturn')}
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogHeader onClose={handleClose}>
        <Typography variant="h6">{t('returns.createReturn')}</Typography>
      </DialogHeader>

      <DialogContent dividers>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="return-po-label">{t('returns.selectPurchaseOrder')}</InputLabel>
          <Select
            labelId="return-po-label"
            label={t('returns.selectPurchaseOrder')}
            value={selectedPoId}
            onChange={(e) => handleSelectPo(e.target.value)}
          >
            {purchaseOrders.map((po) => (
              <MenuItem key={po.id} value={po.id}>
                {po.orderNumber} — {po.supplier.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {!selectedPoId ? (
          <Alert severity="info">{t('returns.selectPurchaseOrderFirst')}</Alert>
        ) : detailQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : detailQuery.isError ? (
          <Alert severity="error">{t('returns.loadError')}</Alert>
        ) : returnableLines.length === 0 ? (
          <Alert severity="info">{t('returns.noReturnableLines')}</Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('returns.selectLinesHint')}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>{t('returns.colPart')}</TableCell>
                    <TableCell align="right">{t('returns.colReceived')}</TableCell>
                    <TableCell align="right">{t('returns.colOnHand')}</TableCell>
                    <TableCell align="right">{t('returns.returnQuantity')}</TableCell>
                    <TableCell>{t('returns.returnReason')}</TableCell>
                    <TableCell align="right">{t('returns.colLineValue')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {returnableLines.map((line) => {
                    const draft = draftFor(line);
                    const cap = returnCap(line);
                    return (
                      <TableRow key={line.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={draft.selected}
                            disabled={cap === 0}
                            onChange={(e) => updateDraft(line, { selected: e.target.checked })}
                            inputProps={{ 'aria-label': line.name }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {line.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                            {line.partNumber}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{line.quantityReceived}</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={cap < line.quantityReceived ? 'warning.main' : 'text.primary'}
                          >
                            {line.quantityOnHand ?? '\u2014'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={draft.quantity}
                            disabled={!draft.selected || cap === 0}
                            onChange={(e) => {
                              const raw = Math.floor(parseFloat(e.target.value) || 0);
                              const clamped = Math.max(1, Math.min(cap, raw));
                              updateDraft(line, { quantity: clamped });
                            }}
                            inputProps={{ min: 1, max: cap, step: 1 }}
                            sx={{ width: 90 }}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 160 }} disabled={!draft.selected}>
                            <Select
                              value={draft.reason}
                              onChange={(e) => updateDraft(line, { reason: e.target.value })}
                            >
                              {REASON_KEYS.map((key) => (
                                <MenuItem key={key} value={key}>
                                  {t(REASON_LABEL_KEY[key] ?? key)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell align="right">
                          {draft.selected ? formatPrice(line.unitCostCents * draft.quantity) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {anyLineCapped && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t('returns.onHandCapHint')}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t('returns.totalReturnValue')}: {formatPrice(totalReturnCents)}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={createReturn.isPending}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={!canSubmit}
          startIcon={createReturn.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('returns.createReturn')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
